import { z } from 'zod';
/**
 * Property location resolution payload.
 *
 * Produced by `resolvePropertyLocation` (api `location-resolver.ts`) after
 * calling Google Place Details + free enrichment APIs (CBSA lookup, FEMA NFHL,
 * Walk Score, US Census tract). Stored in `address_resolutions.resolved` JSONB
 * and injected into `agent_state.dealMetadata[<locationInputKey>]` so the P2
 * completeness gate (`isAddressedLocation`) can verify the field is non-null.
 *
 * Naming: `PropertyResolution` / `propertyResolutionSchema` (NOT `ResolvedLocation`
 * / `resolveLocation` — those names are taken by the market-data domain in
 * `packages/agents/src/lib/market-data/types.ts` + `market-data-enrichment.tool.ts`.
 * ADR 0014 documents the collision and the decision to use distinct names).
 *
 * All fields with |null are degradable — Walk Score / FEMA / Census failures
 * do NOT prevent the location from being considered "addressed" (spec §9 AC).
 * `lat` and `lng` are always present (Place Details HARD-failure terminates
 * resolution; a miss returns `err(AppError.unprocessable(...))` — spec §9 "does
 * NOT silently degrade"). (conversation-model-versioning Phase 4 Session 1)
 */
/**
 * Granularity of a `PropertyResolution`.
 *
 * **2026-05-20 (variables-counter-coherence Inc-C)**: location inputs accept
 * three granularity levels to support a two-step capture flow:
 *
 * - **`'city'`** — MINIMUM acceptable. User typed a city/state token like
 *   `"Nashville, TN"`. `placeId` + `formattedAddress` are NULL; `city` + `state`
 *   + `lat` + `lng` are populated via the free US Census geocoder (no Google
 *   spend). Market-data lookups work at city / metro tier. Agent SHOULD
 *   prompt the user to narrow to a sub-market or full address but MUST NOT
 *   block the conversation — city granularity satisfies the completeness gate.
 *
 * - **`'submarket'`** — PREFERRED. User specified a named neighborhood / area
 *   within a city (e.g. `"Music Row, Nashville TN"`, `"Midtown Manhattan"`).
 *   `submarket` is non-null; `placeId` MAY be null when the resolver couldn't
 *   uniquely place the area on Google Places (free-text neighborhood names
 *   are often ambiguous to Places Text Search). City + state + lat/lng
 *   populated.
 *
 * - **`'address'`** — ULTIMATELY preferred (but not required). Full street
 *   address resolved through Google Place Details — all fields populated
 *   including `placeId` + `formattedAddress` + `line1` + `zip`. Original
 *   pre-Inc-C behavior — back-compat default for any caller that doesn't
 *   pass `granularity`.
 */
export const locationGranularitySchema = z.enum([
    'city',
    'submarket',
    'address',
]);
export const propertyResolutionSchema = z
    .object({
    /**
     * Granularity of this resolution. **Back-compat**: optional + defaults to
     * `'address'` so every pre-Inc-C row stored in `address_resolutions.resolved`
     * (all of which were full Place-Details resolutions) parses correctly
     * without a backfill. New city/submarket resolutions stamp this explicitly.
     */
    granularity: locationGranularitySchema.default('address'),
    /**
     * Google Places place_id — canonical cache key for full-address
     * resolutions. **2026-05-20 (Inc-C)**: nullable to support `'city'` /
     * `'submarket'` granularity where the user didn't pick a specific address.
     * Pre-Inc-C consumers that assumed non-null MUST guard before reading
     * (typecheck enforces this since the type widens to `string | null`).
     * Empty string is rejected — use null instead.
     */
    placeId: z
        .string()
        .min(1, 'placeId must be non-empty when present')
        .nullable(),
    /**
     * Full formatted address from Google Place Details. **2026-05-20
     * (Inc-C)**: nullable for `'city'` / `'submarket'` granularity (no Place
     * Details call was made). For city/submarket granularity, callers
     * synthesize a display string from `city, state` or `submarket, city,
     * state` if they need a label. Empty string is rejected — use null instead.
     */
    formattedAddress: z
        .string()
        .min(1, 'formattedAddress must be non-empty when present')
        .nullable(),
    /** Street line 1 (from address components). Null when unavailable. */
    line1: z.string().nullable(),
    /** City (locality from address components). Null when unavailable. */
    city: z.string().nullable(),
    /** Two-letter state abbreviation. Null when unavailable. */
    state: z.string().nullable(),
    /** ZIP code (5-digit). Null when unavailable. */
    zip: z.string().nullable(),
    /** Latitude from Google Place Details geometry. Always present on success. */
    lat: z.number(),
    /** Longitude from Google Place Details geometry. Always present on success. */
    lng: z.number(),
    /**
     * CBSA (Core-Based Statistical Area) code from `lookupCbsaByZip`.
     * Null when zip is unavailable or no CBSA match found.
     */
    cbsa: z.string().nullable(),
    /**
     * Human-readable metro/CBSA area name from `lookupCbsaByZip`.
     * Feeds `dealMetadata.market` (spec §7). Null when no CBSA match.
     */
    metro: z.string().nullable(),
    /**
     * Submarket override passed by the caller (e.g. from `set_property_address`
     * `submarket_override` param). No polygon inference — always caller-supplied or null.
     */
    submarket: z.string().nullable(),
    /**
     * FEMA NFHL SFHA flood-zone code (e.g. "X", "AE", "A", "VE").
     * Null on FEMA API timeout/failure (degradable).
     */
    floodZone: z.string().nullable(),
    /**
     * Human-readable flood-zone label (e.g. "Minimal flood risk",
     * "1% annual chance flood zone"). Null when floodZone is null.
     */
    floodZoneLabel: z.string().nullable(),
    /**
     * Walk Score (0–100). Null on API 429 / timeout / failure (degradable).
     */
    walkScore: z.number().nullable(),
    /**
     * US Census tract GEOID from the `geographies/coordinates` endpoint.
     * Null on Census API failure (degradable).
     */
    censusTract: z.string().nullable(),
})
    .strict();
